import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listAccounts, createAccount, updateAccount, deleteAccount, refreshAccount,
  type FirebaseAccount,
} from '../api/firebase';
import { useCanEdit } from '../components/EditOnly';
import { FirebaseOrgs } from './firebase/FirebaseOrgs';
import { useAuthStore } from '../store/auth';

function relTime(ts: number | null): string {
  if (!ts) return '—';
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function Firebase() {
  const canEdit = useCanEdit();
  const admin = useAuthStore((s) => s.admin);
  const canManage = canEdit && (admin?.role === 'super_admin' || !!admin?.addons?.includes('developer'));
  const [items, setItems] = useState<FirebaseAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '', ga4_property_id: '', sa_json: '',
    web_api_key: '', web_sender_id: '', web_app_id: '', web_vapid_key: '',
  });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FirebaseAccount | null>(null);
  const [editName, setEditName] = useState('');
  const [editWeb, setEditWeb] = useState({ web_api_key: '', web_sender_id: '', web_app_id: '', web_vapid_key: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    listAccounts().then((data) => setItems(data ?? [])).finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const onAdd = async () => {
    setAdding(true); setAddError(null);
    try {
      await createAccount(form);
      setShowAdd(false);
      setForm({ name: '', ga4_property_id: '', sa_json: '', web_api_key: '', web_sender_id: '', web_app_id: '', web_vapid_key: '' });
      reload();
    } catch (e: any) {
      setAddError(e?.message ?? 'failed');
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (a: FirebaseAccount) => {
    setEditing(a);
    setEditName(a.name);
    setEditWeb({
      web_api_key: a.web_api_key ?? '', web_sender_id: a.web_sender_id ?? '',
      web_app_id: a.web_app_id ?? '', web_vapid_key: a.web_vapid_key ?? '',
    });
    setEditError(null);
  };

  const onSaveEdit = async () => {
    if (!editing) return;
    const name = editName.trim();
    if (!name) { setEditError('Name is required'); return; }
    setSavingEdit(true); setEditError(null);
    try {
      await updateAccount(editing.id, { name, ...editWeb });
      setEditing(null);
      reload();
    } catch (e: any) {
      setEditError(e?.message ?? 'failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const onFile = (file: File) => {
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, sa_json: String(r.result ?? '') }));
    r.readAsText(file);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Firebase</h1>
        {canManage && <button
          onClick={() => setShowAdd(true)}
          className="bg-info hover:bg-info text-white px-4 py-2 rounded-md text-sm"
        >
          + Add Firebase Account
        </button>}
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">GA4 Property</th>
                <th className="text-left p-3">Service Account</th>
                <th className="text-left p-3">Last Sync</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="p-3">
                    <Link to={`/firebase/${a.id}`} className="text-info hover:underline">{a.name}</Link>
                  </td>
                  <td className="p-3 font-mono text-xs">{a.ga4_property_id}</td>
                  <td className="p-3 font-mono text-xs">{a.sa_client_email}</td>
                  <td className="p-3">{relTime(a.last_synced_at)}</td>
                  <td className="p-3">
                    {a.last_error ? (
                      <span className="text-danger" title={a.last_error}>⚠ error</span>
                    ) : a.last_synced_at ? (
                      <span className="text-success">✓ ok</span>
                    ) : (
                      <span className="text-content-subtle">pending</span>
                    )}
                  </td>
                  <td className="p-3 text-right space-x-2">
                    {canManage ? <>
                      <button onClick={() => refreshAccount(a.id).then(reload)} className="text-info hover:underline text-xs">Refresh</button>
                      <button onClick={() => openEdit(a)} className="text-content-muted hover:underline text-xs">Edit</button>
                      <button
                        onClick={() => { if (confirm(`Delete ${a.name}?`)) deleteAccount(a.id).then(reload); }}
                        className="text-danger hover:underline text-xs">Delete</button>
                    </> : <span className="text-content-subtle">—</span>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td className="p-6 text-center text-content-subtle" colSpan={6}>No accounts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <FirebaseOrgs canManage={canManage} />

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">Edit Firebase Account</h2>
            <div className="space-y-3">
              <label className="block">
                <span className="text-sm text-content-muted">Name</span>
                <input
                  autoFocus
                  className="mt-1 w-full border border-border-strong rounded px-3 py-2"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSaveEdit(); }}
                />
              </label>
              <div className="text-xs text-content-subtle">
                GA4 Property: <span className="font-mono">{editing.ga4_property_id}</span>
              </div>
              <div className="pt-2 border-t border-border mt-2">
                <p className="text-sm font-medium text-content mb-1">Web push (browser test link)</p>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="Web API key" value={editWeb.web_api_key}
                  onChange={(e) => setEditWeb({ ...editWeb, web_api_key: e.target.value })} />
                <input className="mt-2 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="messagingSenderId" value={editWeb.web_sender_id}
                  onChange={(e) => setEditWeb({ ...editWeb, web_sender_id: e.target.value })} />
                <input className="mt-2 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="appId" value={editWeb.web_app_id}
                  onChange={(e) => setEditWeb({ ...editWeb, web_app_id: e.target.value })} />
                <input className="mt-2 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="VAPID key" value={editWeb.web_vapid_key}
                  onChange={(e) => setEditWeb({ ...editWeb, web_vapid_key: e.target.value })} />
              </div>
              {editError && <div className="text-danger text-sm">{editError}</div>}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancel</button>
              <button onClick={onSaveEdit} disabled={savingEdit}
                className="bg-info hover:bg-info text-white px-4 py-2 rounded text-sm disabled:opacity-50">
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Add Firebase Account</h2>
            <div className="space-y-3">
              <label className="block">
                <span className="text-sm text-content-muted">Name</span>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm text-content-muted">GA4 Property ID</span>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono"
                  placeholder="properties/123456789"
                  value={form.ga4_property_id} onChange={(e) => setForm({ ...form, ga4_property_id: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm text-content-muted">Service Account JSON</span>
                <textarea className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs h-32"
                  value={form.sa_json} onChange={(e) => setForm({ ...form, sa_json: e.target.value })} />
                <input type="file" accept="application/json" className="mt-2 text-xs"
                  onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
              </label>
              <div className="pt-2 border-t border-border mt-2">
                <p className="text-sm font-medium text-content mb-1">Web push (optional — for the browser test link)</p>
                <p className="text-xs text-content-subtle mb-2">From Firebase Console → Project settings → General (Web app) and Cloud Messaging → Web configuration.</p>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="Web API key" value={form.web_api_key}
                  onChange={(e) => setForm({ ...form, web_api_key: e.target.value })} />
                <input className="mt-2 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="messagingSenderId" value={form.web_sender_id}
                  onChange={(e) => setForm({ ...form, web_sender_id: e.target.value })} />
                <input className="mt-2 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="appId" value={form.web_app_id}
                  onChange={(e) => setForm({ ...form, web_app_id: e.target.value })} />
                <input className="mt-2 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs"
                  placeholder="VAPID key (Web Push certificate)" value={form.web_vapid_key}
                  onChange={(e) => setForm({ ...form, web_vapid_key: e.target.value })} />
              </div>
              {addError && <div className="text-danger text-sm">{addError}</div>}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button onClick={onAdd} disabled={adding}
                className="bg-info hover:bg-info text-white px-4 py-2 rounded text-sm disabled:opacity-50">
                {adding ? 'Testing…' : 'Test & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
